import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskLine } from "../../lib/motion";
import HeroBookletStore from "./HeroBookletStore";
import { api } from "../../lib/api";

const FALLBACK = {
    headline_line1: "Marketing",
    headline_line2: "that's",
    headline_line3_prefix: "actually",
    headline_line3_highlight: "simple",
    subhead: "We help small businesses across Aotearoa and Australia show up online, grow steady, and sell well — with social media support and strategy that fits a real small-business budget.",
    cta_secondary: "Or explore our 3 packages ↓",
};

export default function Hero() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const [content, setContent] = useState(FALLBACK);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    // Layered parallax on decorative blobs & tickers
    const yBlobA = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
    const yBlobB = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);
    const yBlobC = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);
    const yStroke = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -140]);
    const scaleGrid = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);

    useEffect(() => {
        api.get("/site-content/hero")
            .then((r) => setContent({ ...FALLBACK, ...r.data.data }))
            .catch(() => {});
    }, []);

    return (
        <section
            ref={ref}
            className="relative isolate overflow-hidden border-b border-hairline bg-sage"
            data-testid="hero-section"
        >
            {/* Parallax deco blobs */}
            <motion.div
                style={{ y: yBlobA }}
                className="pointer-events-none absolute -top-24 -right-24 -z-10 h-[520px] w-[520px] rounded-full bg-coral/25 blur-3xl"
            />
            <motion.div
                style={{ y: yBlobB }}
                className="pointer-events-none absolute -bottom-40 -left-10 -z-10 h-[460px] w-[460px] rounded-full bg-sun/35 blur-3xl"
            />
            <motion.div
                style={{ y: yBlobC }}
                className="pointer-events-none absolute top-1/3 left-1/2 -z-10 h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-sage-deep/25 blur-3xl"
            />
            <motion.div
                style={{ scale: scaleGrid }}
                className="pointer-events-none absolute inset-0 -z-10 hairline-grid opacity-60"
            />
            <div className="paper-noise absolute inset-0 -z-10 opacity-40" />

            {/* Ambient huge outline word — parallax slower */}
            <motion.div
                style={{ y: yStroke }}
                className="pointer-events-none absolute inset-x-0 top-24 -z-0 select-none text-center"
            >
                <span className="block font-display text-[15vw] font-black uppercase leading-none stroke-text">
                    Aotearoa
                </span>
            </motion.div>

            <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 pb-20 pt-16 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-32 lg:pt-24">
                <div className="lg:col-span-12">
                    <h1
                        className="font-display text-6xl font-black leading-[0.9] tracking-tight text-ink sm:text-7xl lg:text-[9rem]"
                        data-testid="hero-headline"
                    >
                        <MaskLine delay={0.1}>{content.headline_line1}</MaskLine>
                        <MaskLine delay={0.25} className="mt-1">
                            <span>{content.headline_line2}</span>
                        </MaskLine>
                        <MaskLine delay={0.4} className="mt-1">
                            <span>{content.headline_line3_prefix}{" "}
                                <span className="relative inline-block">
                                    <span className="relative z-10 px-3 text-ink">{content.headline_line3_highlight}.</span>
                                    <motion.span
                                        initial={reduce ? false : { scaleX: 0 }}
                                        animate={reduce ? undefined : { scaleX: 1 }}
                                        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1], delay: 1.2 }}
                                        style={{ transformOrigin: "left" }}
                                        className="absolute inset-x-0 bottom-2 -z-0 h-5 bg-coral lg:h-9"
                                    />
                                </span>
                            </span>
                        </MaskLine>
                    </h1>

                    <motion.p
                        initial={reduce ? false : { opacity: 0, y: 12 }}
                        animate={reduce ? undefined : { opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-10 max-w-2xl font-body text-lg leading-relaxed text-ink/70 lg:text-xl"
                        data-testid="hero-subhead"
                    >
                        {content.subhead}
                    </motion.p>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 12 }}
                        animate={reduce ? undefined : { opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <HeroBookletStore />
                        <div className="mt-6">
                            <a
                                href="#pricing"
                                className="inline-flex items-center gap-2 border-b-2 border-ink/30 pb-1 font-display text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:border-coral hover:text-coral"
                                data-testid="hero-cta-packages"
                            >
                                {content.cta_secondary}
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={reduce ? false : { opacity: 0 }}
                        animate={reduce ? undefined : { opacity: 1 }}
                        transition={{ duration: 1, delay: 1.5 }}
                        className="mt-14 flex items-center gap-4 text-xs uppercase tracking-widest text-ink/70"
                    >
                        <span className="h-px w-10 bg-sage/30" />
                        <span>Month-to-month · no 12-month lock-ins</span>
                    </motion.div>
                </div>
            </div>

                {/* Slow editorial marquee — the ONE we keep */}
            <div className="overflow-hidden border-t-2 border-ink bg-sage py-4">
                <div className="flex w-max animate-marquee gap-14 whitespace-nowrap text-ink">
                    {Array.from({ length: 3 }).flatMap((_, i) =>
                        [
                            "Cafés",
                            "Builders",
                            "Boutiques",
                            "Coaches",
                            "Salons",
                            "Studios",
                            "Makers",
                            "Wellness",
                            "Retail",
                            "Florists",
                            "Educators",
                            "Gyms",
                        ].map((w, j) => (
                            <span
                                key={`${i}-${j}`}
                                className="flex items-center gap-14 font-serif text-3xl uppercase italic tracking-tight lg:text-4xl"
                            >
                                <span>{w}</span>
                                <span aria-hidden className="text-coral">
                                    ✦
                                </span>
                            </span>
                        )),
                    )}
                </div>
            </div>
        </section>
    );
}
